import type { InvestmentStatus } from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import type { EntityManagementActions } from "$root/components/spawnable/entity-management/actions";
import { spawnDeleteDialog } from "$root/components/spawnable/entity-management/delete-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import { platformToast } from "$root/notification-system/toast";
import { ToastableError } from "$root/utils";
import { t } from "i18next";
import { useMemo } from "react";
import { aclByArea } from "../acl/checkers/all";
import { referenceRepository } from "../portfolio/references-repository";
import { useUserValue } from "../user";

export function useBenchmarkEntityManagementActions(
	benchmark:
		| {
				name?: string;
				uuid?: string;
				richAcl?: { acl?: RichAccessControl[] };
				status?: InvestmentStatus;
		  }
		| null
		| undefined,
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
	},
): Partial<Omit<EntityManagementActions, "createAsync">> {
	const user = useUserValue();
	return useMemo(
		() => (!benchmark ? {} : benchmarkEntityManagementActions(user, benchmark, callbacks)),
		[callbacks, benchmark, user],
	);
}

export function benchmarkEntityManagementActions(
	user: { id: string },
	benchmark: {
		name?: string;
		uuid?: string;
		richAcl?: { acl?: RichAccessControl[] };
		status?: InvestmentStatus;
	},
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
	},
): Partial<Omit<EntityManagementActions, "createAsync">> {
	return {
		deleteAsync:
			!aclByArea.benchmark.canDelete(user.id, benchmark?.richAcl?.acl ?? []) || benchmark?.status === "ERROR"
				? undefined
				: () =>
						spawnDeleteDialog({
							entityName: benchmark?.name ?? "",
							entityType: "benchmark",
							yesButton: "Proceed",
							noButton: "Cancel",
							onDeleteAsync: () =>
								runWithErrorReporting(
									async () => {
										try {
											await referenceRepository.deleteBenchmark(benchmark.uuid ?? "");

											platformToast({
												children: t("PUSH_NOTIFICATION.SUCCESS_DELETE_CUSTOM_BENCHMARK", { name: benchmark.name }),
												severity: "success",
												icon: "Portfolio",
											});
											callbacks?.onDelete?.(benchmark.uuid ?? "");
										} catch (error) {
											throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
												cause: error,
												icon: "Portfolio",
											});
										}
									},
									{
										area: "benchmark",
										attemptedOperation: {
											message: `delete benchmark "${benchmark.uuid}"`,
											payload: JSON.stringify({ benchmark }),
										},
									},
								),
						}),
		renameAsync:
			!aclByArea.benchmark.canRename(user.id, benchmark?.richAcl?.acl ?? []) || benchmark?.status === "ERROR"
				? undefined
				: () =>
						spawnRenameDialog({
							entityType: "benchmark",
							currentName: benchmark?.name ?? "",
							checkIfNameIsAvailable: referenceRepository.isBenchmarkNameAvailable,
							onSubmitAsync(name) {
								return runWithErrorReporting(
									async () => {
										try {
											await referenceRepository.renameBenchmark(benchmark.uuid ?? "", name);

											platformToast({
												children: t("PUSH_NOTIFICATION.SUCCESS_RENAMED_CUSTOM_BENCHMARK", { name }),
												severity: "success",
												icon: "Portfolio",
											});
											callbacks?.onRename?.(name);

											return name;
										} catch (error) {
											throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
												cause: error,
												icon: "Portfolio",
											});
										}
									},
									{
										area: "benchmark",
										attemptedOperation: {
											message: `rename benchmark "${benchmark.uuid}"`,
											payload: JSON.stringify({ benchmark }),
										},
									},
								);
							},
						}),
	};
}
